import { useQueryClient } from '@tanstack/react-query';
import { type DraftNodeEnvelope, type MergeDraftNodesRequest } from '@bliks-io/contracts';
import { mergeDraftNodesApi } from '../../persist/draft';
import { useDraftMutation } from '../helpers/draftMutation';
import { applyDraftCache } from '../helpers/draftCacheHelpers';
import { reconcileEnvelope } from '../../draftGraph';

/**
 * POST /draft/nodes/merge — fold the merged draft node into the survivor.
 *
 * The single envelope reconcile handles everything in one shot: the survivor
 * appears under its (possibly cross-kind) `nodeKind` so `reconcileEnvelope`
 * evicts the old sibling-array entry, the merged node is dropped via
 * `deletedNodeIds`, and rewired / recomputed neighbors are upserted. No
 * `onMutate` — the merge cascade is too broad to optimistically mirror; the
 * envelope reconcile plus the settle-invalidate backstop converge the cache.
 */
export function useMergeDraftNodes(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: (body: MergeDraftNodesRequest): Promise<DraftNodeEnvelope> =>
      mergeDraftNodesApi(processId, body),
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}
